import React, { useEffect, useState } from 'react';
import { surveyApi as axios } from '../../axiosInstance';
import matchingApi from '../../axiosInstance';
import { useTranslation } from 'react-i18next';
import Scale5RadarChart from '../../components/Scale5RadarChart';
import { QRCodeSVG } from 'qrcode.react';

/* ────────────────────────────────────────────────────────────────
   NEU: Survey-Session robust sicherstellen + 401/403-Retry
   ──────────────────────────────────────────────────────────────── */

async function ensureSurveySessionReady() {
  try {
    // 1) Schon eingeloggt?
    const who = await fetch('/survey/whoami', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .catch(() => null);
    if (who?.hasUser) return;

    // 2) Session via Mentoring anheften
    const tp = await fetch('/mentoring/tenant-package', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .catch(() => null);

    if (tp?.email && tp?.customer_id) {
      await fetch('/survey/sso-attach', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            email: tp.email,
            role: 'hr',
            customer_id: tp.customer_id,
            client_id: tp.customer_id,
          },
        }),
      }).catch(() => {});
    }
  } catch {
    // absichtlich still
  }
}

async function withSurveyAuth(requestFn) {
  try {
    return await requestFn();
  } catch (e) {
    const status = e?.response?.status;
    if (status === 401 || status === 403) {
      await ensureSurveySessionReady();
      return await requestFn();
    }
    throw e;
  }
}

/**
 * Aktuelle Projekte (ohne Auswahl-Dropdown)
 *
 * Props:
 * - customerId           : (optional) falls du es später im Parent brauchst
 * - surveyId             : die aktuell gewählte Survey-ID (vom HR-Dashboard-Header)
 * - selectedSurvey       : (optional) wenn der Parent bereits das komplette Survey-Objekt hat
 *                          -> hat Priorität gegenüber surveyId
 * - onSurveyDeleted(id)  : (optional) Callback nach erfolgreichem Löschen
 */
function CurrentSurveysTile({ customerId, surveyId, selectedSurvey, onSurveyDeleted }) {
  const { t } = useTranslation();

  const [survey, setSurvey] = useState(selectedSurvey ?? null);
  const [loading, setLoading] = useState(!selectedSurvey && !!surveyId);
  const [error, setError] = useState('');

  // Hole (nur) das aktuell gewählte Survey, wenn kein selectedSurvey-Objekt übergeben wurde.
  useEffect(() => {
    let cancelled = false;

    async function fetchSurvey() {
      if (selectedSurvey) {
        setSurvey(selectedSurvey);
        setLoading(false);
        setError('');
        return;
      }
      if (!surveyId) {
        setSurvey(null);
        setLoading(false);
        setError('');
        return;
      }

      setLoading(true);
      setError('');
      try {
        // NEU: Session vor dem ersten Call sicherstellen
        await ensureSurveySessionReady();

        const res = await withSurveyAuth(() =>
          axios.get(`/survey/surveys/${surveyId}`, { withCredentials: true })
        );
        if (!cancelled) {
          setSurvey(res.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          setSurvey(null);
          setError(t('surveys.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchSurvey();
    return () => { cancelled = true; };
  }, [surveyId, selectedSurvey, t]);

  return (
    <div className="bg-gray-800 p-6 rounded-2xl shadow-md text-white m-4">
      <h3 className="text-xl font-bold mb-4">{t('surveys.currentProjects')}</h3>

      {loading && <p className="text-gray-300">{t('surveys.loading')}</p>}
      {error && <p className="text-red-500">{error}</p>}

      {!loading && !error && !survey && (
        <p className="text-gray-300">{t('surveys.noSurveySelected')}</p>
      )}

      {!loading && !error && survey && (
        <>
          <SurveyCard
            survey={survey}
            onSurveyDelete={(id) => {
              onSurveyDeleted && onSurveyDeleted(id);
              // Kachel leert sich, bis der Parent eine neue Auswahl setzt
              setSurvey(null);
            }}
          />
          <div className="mt-6" />
        </>
      )}
    </div>
  );
}

function SurveyCard({ survey, onSurveyDelete }) {
  const { t } = useTranslation();
  const [results, setResults] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [showQuestions, setShowQuestions] = useState(false);
  const [openAnswers, setOpenAnswers] = useState([]);
  const [showOpenAnswers, setShowOpenAnswers] = useState(false);

  const [invitationsSent, setInvitationsSent] = useState(survey.invitations_sent || 0);
  const [savingInvitations, setSavingInvitations] = useState(false);

  const [matchingConfig, setMatchingConfig] = useState(null);
  const [matchingConfigLoading, setMatchingConfigLoading] = useState(true);
  const [matchingConfigSaving, setMatchingConfigSaving] = useState(false);
  const [matchingConfigError, setMatchingConfigError] = useState('');

  // Ergebnisse laden
  useEffect(() => {
    let cancelled = false;
    async function loadResults() {
      try {
        const res = await withSurveyAuth(() =>
          axios.get(`/survey/surveys/${survey.survey_id}/results`, { withCredentials: true })
        );
        if (!cancelled) setResults(res.data);
      } catch {
        /* ignorieren */
      }
    }
    loadResults();
    return () => { cancelled = true; };
  }, [survey.survey_id]);

  // Einladungen aus Survey-Objekt übernehmen
  useEffect(() => {
    setInvitationsSent(survey.invitations_sent || 0);
  }, [survey.invitations_sent]);

  // Matching-Konfiguration laden
  useEffect(() => {
    let cancelled = false;
    setMatchingConfigLoading(true);
    setMatchingConfigError('');
    matchingApi
      .get(`/matching/match/config/${survey.survey_id}`, { withCredentials: true })
      .then(res => {
        if (!cancelled) setMatchingConfig(res.data);
      })
      .catch(() => {
        if (!cancelled) setMatchingConfigError('Konfiguration konnte nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setMatchingConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, [survey.survey_id]);

  async function handleMatchingConfigToggle(field, newValue) {
    if (!matchingConfig) return;
    if (matchingConfig.hasConfirmedMatchings) return;
    const prev = matchingConfig;
    const updated = { ...matchingConfig, [field]: newValue };
    setMatchingConfig(updated);
    setMatchingConfigSaving(true);
    setMatchingConfigError('');
    try {
      await matchingApi.put(
        `/matching/match/config/${survey.survey_id}`,
        {
          weightProfile: updated.weightProfile,
          deptKo: updated.deptKo,
          peerMode: updated.peerMode,
        },
        { withCredentials: true }
      );
    } catch {
      setMatchingConfig(prev);
      setMatchingConfigError('Speichern fehlgeschlagen.');
    } finally {
      setMatchingConfigSaving(false);
    }
  }

  async function saveInvitationsSent(newCount) {
    setSavingInvitations(true);
    try {
      await withSurveyAuth(() =>
        axios.patch(
          `/survey/surveys/${survey.survey_id}/invitations`,
          { invitationsSent: newCount },
          { withCredentials: true }
        )
      );
    } catch (err) {
      alert(t('surveys.saveError'));
    }
    setSavingInvitations(false);
  }

  function handleInvitationsChange(e) {
    const value = e.target.value.replace(/\D/g, '');
    setInvitationsSent(value);
  }
  function handleInvitationsBlur() {
    if (invitationsSent !== undefined && invitationsSent !== null && invitationsSent !== '') {
      saveInvitationsSent(Number(invitationsSent));
    }
  }
  function handleInvitationsKeyDown(e) {
    if (e.key === 'Enter') e.target.blur();
  }

  async function handleToggleQuestions() {
    if (!showQuestions && questions.length === 0) {
      try {
        const res = await withSurveyAuth(() =>
          axios.get(`/survey/surveys/${survey.survey_id}`, { withCredentials: true })
        );
        setQuestions(res.data.questions || []);
      } catch {
        /* ignorieren */
      }
    }
    setShowQuestions(!showQuestions);
  }

  async function handleToggleOpenAnswers() {
    if (!showOpenAnswers && openAnswers.length === 0) {
      try {
        const res = await withSurveyAuth(() =>
          axios.get(`/survey/surveys/${survey.survey_id}/analysis-open`, { withCredentials: true })
        );
        setOpenAnswers(res.data.open_analysis || []);
      } catch {
        /* ignorieren */
      }
    }
    setShowOpenAnswers(!showOpenAnswers);
  }

  async function handleDeleteSurvey() {
    const confirmDelete = window.confirm(t('surveys.deleteConfirm', { title: survey.title }));
    if (!confirmDelete) return;

    try {
      await withSurveyAuth(() =>
        axios.delete(`/survey/surveys/${survey.survey_id}`, { withCredentials: true })
      );
      onSurveyDelete && onSurveyDelete(survey.survey_id);
    } catch (err) {
      alert(t('surveys.deleteError'));
    }
  }

  const surveyLink = `${window.location.origin}/#/employee/survey/${survey.survey_id}`;
  const totalResponses = results ? results.total_responses : 0;
  const returnRate = invitationsSent > 0 ? ((totalResponses / invitationsSent) * 100).toFixed(2) : '0.00';

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-6 mb-2">
        <div>
          <p><strong>ID:</strong> {survey.survey_id}</p>
          <p><strong>{t('surveys.title')}:</strong> {survey.title}</p>
          <p><strong>{t('surveys.createdAt')}:</strong> {survey.created_at}</p>

          <div className="flex items-center gap-2 mt-2">
            <label htmlFor={`input-invitations-${survey.survey_id}`} className="text-sm">
              {t('surveys.invitationsSent')}:
            </label>
            <input
              id={`input-invitations-${survey.survey_id}`}
              type="number"
              min={0}
              className="w-20 px-2 py-1 rounded bg-gray-700 text-white border border-gray-600"
              value={invitationsSent}
              onChange={handleInvitationsChange}
              onBlur={handleInvitationsBlur}
              onKeyDown={handleInvitationsKeyDown}
              disabled={savingInvitations}
            />
            {savingInvitations && <span className="text-xs text-gray-400">{t('surveys.saving')}</span>}
          </div>
        </div>

        <div className="flex flex-col items-center bg-gray-700 p-3 rounded-2xl">
          <div className="text-xs text-gray-300 mb-1">{t('surveys.linkForEmployees')}</div>
          <a
            href={surveyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-green-400 text-sm break-all mb-2"
          >
            {surveyLink}
          </a>
          <QRCodeSVG value={surveyLink} size={80} />
        </div>
      </div>

      {results ? (
        <>
          <p><strong>{t('surveys.responses')}:</strong> {totalResponses}</p>
          <p><strong>{t('surveys.returnRate')}:</strong> {returnRate}%</p>
        </>
      ) : (
        <p>{t('surveys.loadingResults')}</p>
      )}

      <div className="my-4">
        <Scale5RadarChart surveyId={survey.survey_id} />
      </div>

      <div className="flex gap-4 mt-4">
        <button
          onClick={handleToggleQuestions}
          className="bg-mentixxgreen text-black font-bold py-2 px-4 rounded-2xl hover:bg-green-400 transition"
        >
          {showQuestions ? t('surveys.hideQuestions') : t('surveys.showQuestions')}
        </button>

        <button
          onClick={handleToggleOpenAnswers}
          className="bg-mentixxgreen text-black font-bold py-2 px-4 rounded-2xl hover:bg-green-400 transition"
        >
          {showOpenAnswers ? t('surveys.hideOpenAnswers') : t('surveys.showOpenAnswers')}
        </button>
      </div>

      {showQuestions && questions.length > 0 && (
        <div className="bg-gray-700 p-4 mt-4 rounded-2xl">
          <h4 className="font-bold mb-2">{t('surveys.questions')}:</h4>
          <ul className="list-disc list-inside">
            {questions.map((q, idx) => (
              <li key={q.question_id}>
                {t('surveys.question')} {idx + 1}: {q.question_text} ({t('surveys.type')}: {q.question_type})
              </li>
            ))}
          </ul>
        </div>
      )}

      {showOpenAnswers && openAnswers.length > 0 && (
        <div className="bg-gray-700 p-4 mt-4 rounded-2xl">
          <h4 className="font-bold mb-2">{t('surveys.openAnswers')}:</h4>
          {openAnswers.map((item, idx) => (
            <div key={item.question_id} className="mb-4">
              <p className="font-semibold">
                {t('surveys.question')} {idx + 1}: {item.question_text}
              </p>
              {item.answers.length > 0 ? (
                <ul className="list-disc list-inside">
                  {item.answers.map((answer, i2) => (
                    <li key={i2}>{answer}</li>
                  ))}
                </ul>
              ) : (
                <p className="italic text-gray-400">{t('surveys.noAnswers')}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="bg-gray-700 p-4 mt-6 rounded-2xl">
        <h4 className="font-bold mb-3">Matching-Konfiguration</h4>

        {matchingConfigLoading && (
          <p className="text-gray-400 text-sm">Lade Konfiguration…</p>
        )}

        {!matchingConfigLoading && matchingConfig && (() => {
          const isLocked = survey.survey_id === 28 || !!matchingConfig.hasConfirmedMatchings;
          const isDisabled = matchingConfigSaving || isLocked;
          return (
            <div className="flex flex-col gap-3">
              <label className={`flex items-center gap-3 ${isLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-mentixxgreen"
                  checked={!!matchingConfig.deptKo}
                  disabled={isDisabled}
                  onChange={e => handleMatchingConfigToggle('deptKo', e.target.checked)}
                />
                <span className="text-sm">Abteilungs-KO</span>
              </label>

              <label className={`flex items-center gap-3 ${isLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-mentixxgreen"
                  checked={!!matchingConfig.peerMode}
                  disabled={isDisabled}
                  onChange={e => handleMatchingConfigToggle('peerMode', e.target.checked)}
                />
                <span className="text-sm">Peer-Modus</span>
              </label>

              {matchingConfigSaving && (
                <span className="text-xs text-gray-400">Speichern…</span>
              )}

              {survey.survey_id === 28 && (
                <span className="text-xs text-gray-400">(schreibgeschützt)</span>
              )}
              {survey.survey_id !== 28 && matchingConfig.hasConfirmedMatchings && (
                <span className="text-sm text-yellow-400">🔒 Programm läuft — Konfiguration gesperrt.</span>
              )}
            </div>
          );
        })()}

        {matchingConfigError && (
          <p className="text-red-400 text-sm mt-2">{matchingConfigError}</p>
        )}
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={handleDeleteSurvey}
          className="bg-red-600 text-white font-bold py-2 px-6 rounded-2xl hover:bg-red-700 transition"
        >
          {t('surveys.deleteProject')}
        </button>
      </div>
    </div>
  );
}

export default CurrentSurveysTile;
